import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PersonalInfoData, ServicesData } from '@/types/profile';

interface OnboardingData {
  personalInfo?: PersonalInfoData;
  services?: ServicesData;
  profileLink?: string;
  linkCode?: string;
}

interface OnboardingStore {
  data: OnboardingData;
  completedSteps: number[];
  pendingProfilePhotoFile: File | null;

  // Actions
  setPersonalInfo: (data: PersonalInfoData) => void;
  setServices: (data: ServicesData) => void;
  setPendingProfilePhotoFile: (file: File | null) => void;
  clearPendingProfilePhotoFile: () => void;
  setProfileLink: (linkCode: string, profileLink: string) => void;
  markStepComplete: (step: number) => void;
  clearOnboarding: () => void;
  isStepComplete: (step: number) => boolean;
}

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      data: {},
      completedSteps: [],
      pendingProfilePhotoFile: null,

      setPersonalInfo: personalInfo =>
        set(state => ({
          data: { ...state.data, personalInfo },
        })),

      setServices: services =>
        set(state => ({
          data: { ...state.data, services },
        })),

      setPendingProfilePhotoFile: file =>
        set({
          pendingProfilePhotoFile: file,
        }),

      clearPendingProfilePhotoFile: () =>
        set({
          pendingProfilePhotoFile: null,
        }),

      setProfileLink: (linkCode, profileLink) =>
        set(state => ({
          data: { ...state.data, linkCode, profileLink },
        })),

      markStepComplete: step =>
        set(state => ({
          completedSteps: state.completedSteps.includes(step)
            ? state.completedSteps
            : [...state.completedSteps, step].sort(),
        })),

      clearOnboarding: () =>
        set({
          data: {},
          completedSteps: [],
          pendingProfilePhotoFile: null,
        }),

      isStepComplete: step => get().completedSteps.includes(step),
    }),
    {
      name: 'onboarding-storage',
      // Only persist the data, not the completed steps (those come from backend)
      partialize: state => ({ data: state.data }),
    }
  )
);
